import { cva } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../lib/cn";

export type CustomToastType =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "neutral";

const toastContainerVariants = cva(
  "pointer-events-auto fixed top-0 left-1/2 z-50 mt-4 -translate-x-1/2 transform overflow-hidden rounded-lg px-4 py-3 shadow-lg",
  {
    variants: {
      type: {
        success: "w-fit max-w-[361px] bg-twi-blue-100 text-twi-blue-800",
        error: "w-full max-w-[361px] bg-twi-red-100 text-twi-red-800",
        warning: "w-full max-w-[361px] bg-twi-yellow-100 text-twi-yellow-800",
        info: "w-full max-w-[361px] bg-white text-black",
        neutral: "w-fit bg-twi-blue-100 text-twi-blue-800",
      },
      clickable: {
        true: "cursor-pointer",
      },
    },
    defaultVariants: {
      clickable: false,
    },
  },
);

const toastTitleVariants = cva("font-semibold text-base", {
  variants: {
    type: {
      success: "text-nowrap",
      error: "line-clamp-2",
      warning: "line-clamp-2",
      info: "line-clamp-2",
      neutral: "text-nowrap",
    },
  },
});

const toastProgressVariants = cva("h-full", {
  variants: {
    type: {
      success: "",
      error: "bg-twi-red-600",
      warning: "bg-twi-yellow-700",
      info: "bg-[linear-gradient(101deg,#4285F3_-5.36%,#F48A4D_104.65%)]",
      neutral: "",
    },
  },
});

export interface CustomToastProps {
  message: string;
  description?: string;
  icon?: React.ElementType;
  type: CustomToastType;
  action?: {
    label?: string;
    icon?: React.ElementType;
    ariaLabel?: string;
    onClick?: () => void;
    closeOnClick?: boolean;
  };
  duration: number;
  testId: string;
  filled?: boolean;
  redirect?: () => void;
}

export function CustomToast({
  message,
  description,
  icon,
  type,
  action,
  duration,
  testId,
  filled,
  redirect,
}: CustomToastProps) {
  const Icon = icon;
  const ActionIcon = action?.icon;

  const iconColors: Record<CustomToastType, string> = {
    success: "#183462",
    error: "#63221E",
    warning: "#625028",
    info: "#000000",
    neutral: "#183462",
  };

  function onActionClick(e: React.MouseEvent) {
    e.stopPropagation();
    action?.onClick?.();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!redirect) {
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      redirect();
    }
  }

  const interactiveProps = redirect
    ? {
        onClick: redirect,
        onKeyDown,
        role: "button" as const,
        tabIndex: 0,
      }
    : {};

  const showProgressBar = type !== "success" && type !== "neutral";

  return (
    <div
      className={cn(
        toastContainerVariants({ type, clickable: Boolean(redirect) }),
      )}
      data-testid={testId}
      {...interactiveProps}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon ? (
            <Icon
              color={iconColors[type]}
              height={20}
              width={20}
              filled={filled ? true : undefined}
              className="shrink-0"
            />
          ) : (
            <img src="sing+.svg" alt="Sing+" className="size-8 invert" />
          )}
          <div className="text-left">
            <p className={cn(toastTitleVariants({ type }))}>{message}</p>
            {description && (
              <p className="line-clamp-2 text-sm">{description}</p>
            )}
          </div>
        </div>

        {action && (
          <button
            type="button"
            onClick={onActionClick}
            className="pointer-events-auto ml-4 flex cursor-pointer items-center text-nowrap font-semibold text-sm hover:opacity-80"
            aria-label={action.ariaLabel ?? action.label ?? "Toast action"}
            data-testid={
              action.label === "UNDO"
                ? "undo-favorite-button"
                : "toast-action-button"
            }
          >
            {ActionIcon && (
              <ActionIcon color={iconColors[type]} height={20} width={20} />
            )}
            {action.label}
          </button>
        )}
      </div>

      {showProgressBar && (
        <div className="absolute bottom-0 left-0 h-[4px] w-full">
          <div
            className={cn(toastProgressVariants({ type }))}
            style={{
              animation: `toast-progress ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}
